import React, { useCallback, useRef, useState } from 'react';
import { useDisclosure } from '@withjoy/joykit';
import { createContext } from '@shared/utils/createContext';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsRestrictedShippingRegion } from '@apps/card/hooks/useIsRestrictedShippingRegion';
import { RestrictedInternationalShippingWarningDialog } from './RestrictedInternationalShippingWarningDialog';
import type { StationeryTemplateIdentifiers } from './types';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { StationeryDraftFormat } from '@graphql/generated-base';

type RestrictedInternationalShippingWarningContext = {
  /**
   * If the buyer's region is not one card_service sells to (see useIsRestrictedShippingRegion), show a warning dialog.
   * Else, call the nextDialogCallback.
   */
  checkIfUserIsRestricted: (
    draftFormat: StationeryDraftFormat,
    templateIdentifiers: StationeryTemplateIdentifiers,
    onShowWarningDialog: () => void,
    nextDialogCallback?: () => void,
    /**
     * Called when the user dismisses the dialog instead of confirming. Most callers can ignore it:
     * their page is still there behind the dialog. A caller that replaced its page to get here —
     * the envelope entry route, which renders only a loader — must know, or dismissing strands the
     * user on that loader.
     */
    onDismiss?: () => void,
  ) => void;
};

const [Provider, useRestrictedInternationalShippingWarningContext] = createContext<RestrictedInternationalShippingWarningContext>({
  name: 'RestrictedInternationalShippingWarning',
});

const RestrictedInternationalShippingWarningProvider: React.FC<{ eventId: string | undefined }> = ({ children, eventId: _eventId }) => {
  const isUserRestricted = useIsRestrictedShippingRegion();
  //TODO: split telemetry by draftFormat
  const { buttonInteracted } = useCardTelemetry();
  const { isOpen: isOpenPrint, onOpen: onOpenPrint, onClose: onClosePrint } = useDisclosure();
  const { isOpen: isOpenDigital, onOpen: onOpenDigital, onClose: onCloseDigital } = useDisclosure();

  // Store the nextDialogCallback in a ref so that it can be called when the user
  // confirms they want to move forward in the warning dialog
  const nextDialogCallbackRef = useRef<(() => void) | null | undefined>(null);

  // Cleared as soon as it fires, and on confirm, so a later dismissal cannot replay the previous
  // caller's handler.
  const onDismissRef = useRef<(() => void) | null>(null);

  const [currentTemplateIdentifiers, setCurrentTemplateIdentifiers] = useState<StationeryTemplateIdentifiers | null>(null);

  const checkIfUserIsRestricted = useCallback<RestrictedInternationalShippingWarningContext['checkIfUserIsRestricted']>(
    (draftFormat, templateIdentifiers, onShowWarningDialog, nextDialogCallback, onDismiss) => {
      // If the user is restricted, show the dialog
      if (isUserRestricted) {
        // Store the nextDialogCallback in a ref so that it can be called after the dialog is closed
        // and the user confirms they want to move forward
        nextDialogCallbackRef.current = nextDialogCallback;
        onDismissRef.current = onDismiss ?? null;
        setCurrentTemplateIdentifiers(templateIdentifiers);
        onShowWarningDialog();
        if (draftFormat === StationeryDraftFormat.print) {
          onOpenPrint();
        } else {
          onOpenDigital();
        }
        return;
      }

      // If the user is not restricted, move forward
      nextDialogCallback?.();
    },
    [isUserRestricted, onOpenPrint, onOpenDigital],
  );

  const handleOnClickPrint = useEventCallback(() => {
    onDismissRef.current = null;
    onClosePrint();
    if (currentTemplateIdentifiers?.themeId) {
      buttonInteracted({ name: 'restrictedInternationalShippingWarningDialog_confirmClicked', themeId: currentTemplateIdentifiers.themeId });
    }
    // Call the nextDialogCallback that we collected when we determined that the user was restricted
    // and should see the dialog
    nextDialogCallbackRef.current?.();
  });

  const handleOnClickDigital = useEventCallback(() => {
    onDismissRef.current = null;
    onCloseDigital();
    if (currentTemplateIdentifiers?.themeId) {
      buttonInteracted({ name: 'restrictedInternationalShippingWarningDialog_confirmClicked', themeId: currentTemplateIdentifiers.themeId });
    }
    // Call the nextDialogCallback that we collected when we determined that the user was restricted
    // and should see the dialog
    nextDialogCallbackRef.current?.();
  });

  const notifyDismissed = () => {
    const onDismiss = onDismissRef.current;
    onDismissRef.current = null;
    onDismiss?.();
  };

  const handleOnClosePrint = () => {
    if (currentTemplateIdentifiers?.themeId) {
      buttonInteracted({ name: 'restrictedInternationalShippingWarningDialog_dismiss', themeId: currentTemplateIdentifiers.themeId });
    }
    onClosePrint();
    notifyDismissed();
  };

  const handleOnCloseDigital = () => {
    if (currentTemplateIdentifiers?.themeId) {
      buttonInteracted({ name: 'restrictedInternationalShippingWarningDialog_dismiss', themeId: currentTemplateIdentifiers.themeId });
    }
    onCloseDigital();
    notifyDismissed();
  };

  return (
    <Provider value={{ checkIfUserIsRestricted }}>
      {children}
      <RestrictedInternationalShippingWarningDialog
        isOpen={isOpenPrint}
        templateIdentifiers={currentTemplateIdentifiers}
        handleOnClose={handleOnClosePrint}
        handleOnClick={handleOnClickPrint}
        draftFormat={StationeryDraftFormat.print}
      />
      <RestrictedInternationalShippingWarningDialog
        isOpen={isOpenDigital}
        templateIdentifiers={currentTemplateIdentifiers}
        handleOnClose={handleOnCloseDigital}
        handleOnClick={handleOnClickDigital}
        draftFormat={StationeryDraftFormat.digital}
      />
    </Provider>
  );
};

export { RestrictedInternationalShippingWarningProvider, useRestrictedInternationalShippingWarningContext };
